import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Motion from '@/components/base/motion'
import ParallaxImage from '@/components/base/parallax-image'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { imageTextRevealAnimation } from '@/components/sections/animations'
import Spacer from '@/components/sections/spacer'

import { cn } from '@/lib/utils'
import { WPLink, WPMedia } from '@/types'

export type ImageTextProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    pretitle: string
    title: string
    media: WPMedia
    text: string
    alignment: 'left' | 'right'
    link_1: WPLink
    link_2: WPLink
  }>

const ImageText = ({
  pretitle,
  title,
  media,
  text,
  link_1,
  link_2,
  alignment = 'right',
  className,
  ...props
}: ImageTextProps) => {
  const isTextRight = alignment === 'right'

  const hasContent =
    pretitle || title || text || link_1?.url || link_2?.url || false

  return (
    <Motion animation={imageTextRevealAnimation}>
      {(motionRef) => (
        <Section
          ref={motionRef}
          className={cn(className)}
          {...props}
          data-alignment={alignment}
        >
          <Spacer variant="xl" />
          <Container>
            <Grid className="items-start gap-y-spacing-2xl">
              <div
                className={cn(
                  'col-span-2 mt-0 md:col-span-6 lg:col-span-5 lg:row-start-1',
                  {
                    'md:mt-[160px]': pretitle || text,
                    'lg:col-start-1': isTextRight,
                    'lg:col-start-8': !isTextRight
                  }
                )}
              >
                {(media?.poster?.url || media?.video?.url) && (
                  <ParallaxImage
                    offset={-40}
                    data-motion-media
                    className={cn(
                      'w-full translate-x-0 lg:w-[calc(100%+6rem)] xl:w-[calc(100%+10rem)]',
                      {
                        'w-full lg:w-full xl:w-full': isTextRight
                      }
                    )}
                    style={{
                      aspectRatio: `${media.poster.width} / ${media.poster.height}`
                    }}
                  >
                    <Media
                      media={media}
                      data-lightbox
                      className={cn(
                        'w-full translate-x-0 lg:w-[calc(100%+6rem)] xl:w-[calc(100%+10rem)]',
                        {
                          'lg:translate-x-[-6rem] xl:translate-x-[-10rem]':
                            isTextRight
                        }
                      )}
                    />
                  </ParallaxImage>
                )}
              </div>
              {hasContent ? (
                <div
                  className={cn(
                    'col-span-2 grid gap-y-spacing-lg md:col-span-6 lg:col-span-5',
                    {
                      'lg:col-start-8': isTextRight,
                      'lg:col-start-1 lg:row-start-1': !isTextRight
                    }
                  )}
                >
                  {pretitle ? (
                    <Typography
                      variant="regular"
                      className="condensed text-text-primary-light dark:text-text-primary-dark"
                      data-motion-pretitle
                    >
                      {pretitle}
                    </Typography>
                  ) : null}

                  {title ? (
                    <Typography
                      variant="h3"
                      component="h2"
                      data-motion-title
                      className="text-s-h3 md:text-s-h2"
                    >
                      {title}
                    </Typography>
                  ) : null}

                  {text ? (
                    <Typography
                      variant="regular"
                      paragraph
                      data-motion-text
                      className="text-regular md:text-large text-text-primary-light dark:text-text-primary-dark"
                    >
                      {text}
                    </Typography>
                  ) : null}

                  <div
                    className="flex flex-wrap gap-spacing-2xs place-self-start"
                    data-motion-ctas
                  >
                    {link_1?.url ? (
                      <CtaButton
                        href={link_1.url}
                        target={link_1.target}
                        variant="primary"
                        typography="regular"
                        className={cn('md:min-w-0', {
                          'min-w-[200px]': link_2?.url
                        })}
                        data-motion-button
                      >
                        {link_1.title}
                      </CtaButton>
                    ) : null}

                    {link_2?.url ? (
                      <CtaButton
                        href={link_2.url}
                        target={link_2.target}
                        variant="secondary"
                        typography="regular"
                        className={cn('md:min-w-0', {
                          'min-w-[200px]': link_1?.url
                        })}
                      >
                        {link_2.title}
                      </CtaButton>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </Grid>
          </Container>
          <Spacer variant="xl" />
        </Section>
      )}
    </Motion>
  )
}

export default ImageText
